import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { addView, listViews, removeView, SavedView } from '../utils/savedViews';
import { useAnchoredPopover } from '../hooks/useAnchoredPopover';
import { CompareMode, PresetKey } from './DateRangeControl';
import { FilterState } from './FilterBar';

interface Props {
    currentState: {
        preset: PresetKey;
        customFrom: Date | null;
        customTo: Date | null;
        compareMode: CompareMode;
        compareCustomFrom: Date | null;
        compareCustomTo: Date | null;
        filters: FilterState;
    };
    onApply: (view: SavedView) => void;
}

const toYmd = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/**
 * Saved-views chip row. Lists localStorage-persisted view names; clicking a
 * chip applies it. "+ Save current view" opens a small popover with a name
 * field. Hides itself when no saved views exist AND we're not saving — to
 * avoid an empty bar.
 */
const SavedViewsBar: React.FC<Props> = ({ currentState, onApply }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) =>
        intl.formatMessage({ id, defaultMessage });

    const [views, setViews] = useState<SavedView[]>(() => listViews());
    const [saveOpen, setSaveOpen] = useState(false);
    const [name, setName] = useState('');
    const addBtnRef = useRef<HTMLButtonElement>(null);

    const closeSave = useCallback(() => {
        setSaveOpen(false);
        setName('');
    }, []);

    // Save popover portals to the .av2-page root (escapes the toolbar's
    // stacking context, keeps the --av2-* token scope); the hook owns
    // positioning + click-outside + Escape.
    const { panelRef, style: popStyle, portalTarget } = useAnchoredPopover(addBtnRef, saveOpen, closeSave, 'start');

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const saved = addView({
            name: trimmed,
            preset: currentState.preset,
            customFrom: currentState.customFrom ? toYmd(currentState.customFrom) : undefined,
            customTo: currentState.customTo ? toYmd(currentState.customTo) : undefined,
            compareMode: currentState.compareMode,
            compareCustomFrom: currentState.compareCustomFrom
                ? toYmd(currentState.compareCustomFrom) : undefined,
            compareCustomTo: currentState.compareCustomTo
                ? toYmd(currentState.compareCustomTo) : undefined,
            filters: currentState.filters,
        });
        setViews((v) => [...v, saved]);
        setName('');
        setSaveOpen(false);
    };

    const handleDelete = (id: string) => {
        const next = removeView(id);
        setViews(next);
    };

    if (views.length === 0 && !saveOpen) {
        return (
            <div className="av2-saved">
                <span className="av2-controls__label">
                    <FormattedMessage
                        id="analyticsV2.savedViews.label"
                        defaultMessage="Saved views"
                    />
                </span>
                <button
                    type="button"
                    className="av2-saved__add-empty"
                    onClick={() => setSaveOpen(true)}
                >
                    <span aria-hidden="true">+</span>
                    <FormattedMessage
                        id="analyticsV2.savedViews.save"
                        defaultMessage="Save current view"
                    />
                </button>
            </div>
        );
    }

    return (
        <div className="av2-saved">
            <span className="av2-controls__label">
                <FormattedMessage id="analyticsV2.savedViews.label" defaultMessage="Saved views" />
            </span>
            <div className="av2-saved__chips">
                {views.map((view) => (
                    <span key={view.id} className="av2-saved__chip">
                        <button
                            type="button"
                            className="av2-saved__chip-apply"
                            onClick={() => onApply(view)}
                        >
                            {view.name}
                        </button>
                        <button
                            type="button"
                            className="av2-saved__chip-delete"
                            onClick={() => handleDelete(view.id)}
                            title={fm('analyticsV2.savedViews.deleteTooltip', 'Delete')}
                            aria-label={fm('analyticsV2.savedViews.deleteTooltip', 'Delete')}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <div className="av2-saved__add-wrap">
                    <button
                        ref={addBtnRef}
                        type="button"
                        className="av2-saved__add"
                        onClick={() => setSaveOpen((v) => !v)}
                    >
                        <span aria-hidden="true">+</span>
                        <FormattedMessage
                            id="analyticsV2.savedViews.save"
                            defaultMessage="Save current view"
                        />
                    </button>
                    {saveOpen && portalTarget && ReactDOM.createPortal(
                        <div className="av2-pop av2-pop--save" ref={panelRef} style={popStyle}>
                            <input
                                type="text"
                                className="av2-pop__search"
                                placeholder={fm(
                                    'analyticsV2.savedViews.namePlaceholder',
                                    'View name (e.g. Q4 review)',
                                )}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSave();
                                }}
                                autoFocus
                            />
                            <div className="av2-pop__footer">
                                <button
                                    type="button"
                                    className="av2-pop__btn av2-pop__btn--ghost"
                                    onClick={() => {
                                        setSaveOpen(false);
                                        setName('');
                                    }}
                                >
                                    <FormattedMessage
                                        id="analyticsV2.savedViews.cancelBtn"
                                        defaultMessage="Cancel"
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="av2-pop__btn av2-pop__btn--primary"
                                    onClick={handleSave}
                                    disabled={!name.trim()}
                                >
                                    <FormattedMessage
                                        id="analyticsV2.savedViews.saveBtn"
                                        defaultMessage="Save"
                                    />
                                </button>
                            </div>
                        </div>,
                        portalTarget
                    )}
                </div>
            </div>
        </div>
    );
};

export default SavedViewsBar;
